import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { hasTier } from '@/admin/access';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { kindInfo, parseKind } from '@/admin/publishing/kinds';
import { PostEditor } from '@/admin/publishing/PostEditor';
import { StatusBadge } from '@/admin/publishing/StatusBadge';
import buttonStyles from '@/admin/styles/button.module.css';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { PageTitle } from '@/admin/ui/Page';
import { richContentCss } from '@/admin/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import { formatWhen } from '@/admin/ui/Versions';
import api from '@/api';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/admin/posts/$postId')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  component: PostDetail,
  validateSearch: validateEditSearch,
});

function PostDetail() {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const { postId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const query = api.adminGetPost.useQuery({
    path: { post_id: Number(postId) },
  });
  const post = query.data;

  if (query.isLoading)
    return <Message>{t('admin.publishing.post.loading')}</Message>;
  if (query.error || !post)
    return <Message $error>{t('admin.publishing.post.errorLoading')}</Message>;

  // Follow-up tutorials share the table but are not editable (census: dropped).
  const kind = parseKind(post.kind);
  if (!kind)
    return <Message $error>{t('admin.publishing.post.notEditable')}</Message>;
  // The server's 403 is the gate; hiding the controls is courtesy.
  const canEdit = hasTier(me, kindInfo(kind).minTier);

  return (
    <DetailPage>
      <Header>
        <div>
          <Link to="/admin/posts/" search={{ kind }}>
            {t('admin.publishing.post.back')}
          </Link>
          <PageTitle>{post.title}</PageTitle>
        </div>
        {!editing && canEdit && (
          <Button className={buttonStyles.Button} onClick={startEditing}>
            {t('admin.common.edit')}
          </Button>
        )}
      </Header>
      {editing && canEdit ? (
        // key remounts the form when switching posts, so its seeded state stays fresh.
        <PostEditor
          key={post.id}
          post={post}
          kind={kind}
          onDone={stopEditing}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.overview')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>{t('admin.common.id')}</FieldLabel>
                <FieldValue>{post.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.publishing.post.kind')}</FieldLabel>
                <FieldValue>{t(kindInfo(kind).labelKey)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.language')}</FieldLabel>
                <FieldValue>{t(LANGUAGE_LABELS[post.language])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.status')}</FieldLabel>
                <FieldValue>
                  <StatusBadge status={post.status} />
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.slug')}</FieldLabel>
                <FieldValue>{post.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.publishing.post.categories')}</FieldLabel>
                <FieldValue>{post.category_names.join(', ') || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.publishing.post.author')}</FieldLabel>
                <FieldValue>{post.author_name}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.publishing.post.published')}</FieldLabel>
                <FieldValue>{formatWhen(post.published_at)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.publishing.post.updated')}</FieldLabel>
                <FieldValue>
                  {formatWhen(post.updated_at)}
                  {post.updated_by ? ` · ${post.updated_by.name}` : ''}
                </FieldValue>
              </Field>
              {post.public_path && (
                <Field>
                  <FieldLabel>{t('admin.publishing.post.onSite')}</FieldLabel>
                  <FieldValue>
                    {/* A member page outside the console: a full navigation. */}
                    <a href={post.public_path}>
                      {t('admin.publishing.post.viewOnSite')}
                    </a>
                  </FieldValue>
                </Field>
              )}
            </FieldGrid>
          </Section>
          <Section>
            <SectionTitle>{t('admin.publishing.editor.body')}</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: rendering trusted staff-authored post HTML in admin */}
            <Body dangerouslySetInnerHTML={{ __html: post.content }} />
          </Section>
        </>
      )}
    </DetailPage>
  );
}

const Header = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
`;

const Body = styled.div`
  ${richContentCss}
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
